import React, { useState } from 'react';
import { 
  Globe, 
  Search, 
  CheckCircle2, 
  ArrowRight,
  TrendingUp,
  Share2,
  Workflow,
  Sparkles,
  ArrowUpRight,
  Award,
  Megaphone
} from 'lucide-react';

import { ServiceKey } from '../data/serviceDetails';
import { BrandedServiceImage } from './BrandedServiceImage';

interface CorePillarsProps {
  onOpenBooking: () => void;
  onNavigateToService?: (serviceKey: ServiceKey) => void;
}

interface PillarItem {
  key: ServiceKey;
  routeKey: ServiceKey;
  index: string;
  category: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  features: { name: string; desc: string }[];
  metric: string;
  benefit: string;
}

export const CorePillars: React.FC<CorePillarsProps> = ({ 
  onOpenBooking,
  onNavigateToService 
}) => {
  const [activePillar, setActivePillar] = useState<number>(0);

  const pillars: PillarItem[] = [
    {
      key: 'branding',
      routeKey: 'branding',
      index: '01',
      category: '20+ YRS EXPERIENCE',
      title: 'Branding Consultant',
      subtitle: 'Over 20 Years of Hands-On Work & Design',
      description: 'Two decades of hands-on creative direction, corporate identity architecture, and visual design craftsmanship. We shape authoritative brands that command instant trust across print, web, and digital touchpoints.',
      icon: Award,
      features: [
        {
          name: '20+ Years of Hands-On Design Mastery',
          desc: 'Direct senior-level execution across visual identity systems, typography, color psychology, and corporate rebranding.',
        },
        {
          name: 'Complete Brand Identity & Style Guides',
          desc: 'Bespoke logo marks, brand books, executive stationery, packaging, and scalable design systems built for longevity.',
        },
        {
          name: 'Strategic Brand Positioning & Narrative',
          desc: 'Clarifying your market differentiation, value proposition, and voice so high-value clients choose you over competitors.',
        },
        {
          name: 'UI/UX & Cross-Media Graphic Design',
          desc: 'Cohesive visual execution from high-converting digital interfaces to commercial print and environmental graphics.',
        },
        {
          name: 'Brand Audit & Visual Modernization',
          desc: 'Refreshing legacy identities for the 2026 digital and AI era while preserving hard-earned brand equity.',
        },
      ],
      metric: '20+ Years Hands-On',
      benefit: 'Builds unmistakable market authority and elevates perceived brand value across every customer touchpoint.',
    },
    {
      key: 'social',
      routeKey: 'social',
      index: '02',
      category: 'CORE SERVICE',
      title: 'Social Media Consulting',
      subtitle: 'Strategic Audience Growth & Brand Authority',
      description: 'Move beyond random posting to structured social ecosystems that build community trust, amplify executive voice, and drive qualified inbound demand across LinkedIn, Instagram, Facebook, and X.',
      icon: Share2,
      features: [
        {
          name: 'Omnichannel Social Strategy & Audits',
          desc: 'Platform-specific content pillars, audience persona mapping, and competitor benchmarking tailored to your commercial goals.',
        },
        {
          name: 'Visual Content Direction & Brand Cohesion',
          desc: 'Scroll-stopping social graphics, carousel frameworks, and short-form video scripts aligned with your core brand identity.',
        },
        {
          name: 'Profile & Entity Authority Optimization',
          desc: 'Optimizing social bios, links, and metadata so your profiles rank in both social search and AI knowledge graphs.',
        },
        {
          name: 'Community Engagement & Social Selling Workflows',
          desc: 'Structured DM and comment conversion playbooks that turn passive followers into warm sales conversations.',
        },
        {
          name: 'Executive Thought Leadership & Coaching',
          desc: 'Hands-on advisory for founders and in-house teams to maintain a consistent, high-converting social presence.',
        },
      ],
      metric: '3.8x Engagement Lift',
      benefit: 'Transforms social channels from time-consuming obligations into predictable brand equity and inbound pipeline.',
    },
    {
      key: 'ads',
      routeKey: 'ads',
      index: '03',
      category: 'CORE SERVICE',
      title: 'Digital / Smart Advertisement',
      subtitle: 'Data-Driven Paid Media & High-ROI Campaigns',
      description: 'Precision-targeted smart advertising engineered for measurable ROAS. We combine 20+ years of persuasive creative design with algorithmic ad targeting across Google, Meta, LinkedIn, and programmatic networks.',
      icon: Megaphone,
      features: [
        {
          name: 'Smart AI-Assisted Ad Targeting & Bidding',
          desc: 'High-intent audience segmentation across Google Search, Display, YouTube, Meta (Instagram/Facebook), and LinkedIn Ads.',
        },
        {
          name: 'High-Converting Ad Creative & Copy',
          desc: 'Bespoke visual ad creatives, motion graphics, and direct-response copywriting tested for maximum click-through rates.',
        },
        {
          name: 'Full-Funnel Retargeting & Pixel Architecture',
          desc: 'Server-side tracking, Meta Pixel, and Google Tag Manager events that re-engage warm visitors until they convert.',
        },
        {
          name: 'Dedicated Conversion Landing Pages',
          desc: 'Ultra-fast, message-matched campaign landing pages routed directly into your WhatsApp and CRM pipeline.',
        },
        {
          name: 'Real-Time ROAS & Attribution Reporting',
          desc: 'Transparent cost-per-lead (CPL) tracking and continuous budget optimization to eliminate wasted ad spend.',
        },
      ],
      metric: '4.5x Target ROAS',
      benefit: 'Delivers immediate, scalable customer acquisition with full visibility from first ad impression to closed revenue.',
    },
    {
      key: 'websites',
      routeKey: 'websites',
      index: '04',
      category: 'CORE SERVICE',
      title: 'Pro WordPress & AI Websites',
      subtitle: 'Design Pro WordPress Websites That Are Cited by AI',
      description: 'We design high-performance Pro WordPress websites and custom AI-ready web platforms engineered for human conversion and direct citation by ChatGPT, Perplexity, and Google AI Overviews.',
      icon: Globe,
      features: [
        {
          name: 'Pro WordPress Websites Cited by AI',
          desc: 'Bespoke, speed-hardened Pro WordPress builds structured with Schema.org entity graphs and /llms.txt so AI engines cite your brand.',
        },
        {
          name: 'Modern Custom Business Architecture',
          desc: 'Custom-built responsive designs tailored to your visual identity with sub-second page loads (<1s Core Web Vitals).',
        },
        {
          name: 'AI-Assisted Persuasive Copywriting',
          desc: 'Conversion-tuned copy and structured answer nodes that articulate your value proposition and earn AI search citations.',
        },
        {
          name: 'WooCommerce, Corporate & Editorial Scalability',
          desc: 'Enterprise-grade WordPress & headless CMS workflows empowering your team to publish and scale effortlessly.',
        },
        {
          name: 'Conversion-Focused UX & Hostinger Cloud',
          desc: 'High-contrast calls-to-action, sticky WhatsApp bars, and LiteSpeed/Cloud caching for instant global delivery.',
        },
      ],
      metric: '0.8s load · AI Cited',
      benefit: 'Combines the publishing power of Pro WordPress with 2026 GEO architecture so AI engines quote and recommend your business.',
    },
    {
      key: 'search',
      routeKey: 'search',
      index: '05',
      category: 'CORE SERVICE',
      title: 'AI Search Visibility',
      subtitle: 'Make Your Business Discoverable Across Google & AI Search',
      description: 'The search landscape has permanently shifted. In 2026, getting traffic requires mastering both classic Google Search (SEO) and Generative Engine Optimization (GEO) for ChatGPT, Perplexity, and AI Overviews.',
      icon: Search,
      features: [
        {
          name: 'Full JSON-LD Entity Graph',
          desc: 'Comprehensive schema (Organization, Service, FAQPage, LocalBusiness) providing clean machine-readable data to AI models.',
        },
        {
          name: 'Generative Engine Optimization (GEO)',
          desc: 'Engineered content nodes designed to be extracted and cited as the authoritative source by ChatGPT and Perplexity.',
        },
        {
          name: 'Question-Answering Architecture',
          desc: 'Structured FAQ hubs that answer natural language prompts posed by high-intent buyers in modern search engines.',
        },
        {
          name: 'Traditional Google SEO Core',
          desc: 'Semantic HTML5 tags, optimized title tags, meta descriptions, and sitemaps for top-tier Google rankings.',
        },
        {
          name: 'Entity & Authority Optimization',
          desc: 'Establishes clear topical authority, verifying credentials, locations, and specialized offerings for crawlers.',
        },
      ],
      metric: '3.4x more citations',
      benefit: 'Ensures your business is recommended when buyers ask AI engines for top solutions.',
    },
    {
      key: 'automation',
      routeKey: 'automation',
      index: '06',
      category: 'CORE SERVICE',
      title: 'AI Business Automation',
      subtitle: 'Turn Your Website Into an Always-On Sales Engine',
      description: 'Your website shouldn’t just look good—it should work. We connect your website directly into your client acquisition operations, transforming passive clicks into booked pipeline.',
      icon: Workflow,
      features: [
        {
          name: '24/7 Custom AI Concierge Chatbot',
          desc: 'Trained exclusively on your business knowledge base to guide visitors, answer inquiries, and schedule appointments.',
        },
        {
          name: 'Lead Qualification Automation',
          desc: 'Captures and scores project scope, budget, and contact info before forwarding to your sales team.',
        },
        {
          name: 'Instant WhatsApp Routing (<60s SLA)',
          desc: 'Transfers inbound inquiries straight to WhatsApp with immediate confirmation to prevent lead drop-off.',
        },
        {
          name: 'Two-Way CRM Synchronization',
          desc: 'Pushes validated prospects directly into HubSpot, Salesforce, Pipedrive, Zoho, or Notion with full context.',
        },
        {
          name: 'Automated Calendar Scheduling',
          desc: 'Eliminates email tag with direct Google Calendar / Outlook integration for instant consultations.',
        },
      ],
      metric: '<60s response latency',
      benefit: 'Turns 3-day slow email follow-ups into instant qualified revenue.',
    },
  ];

  const handleCardClick = (idx: number, pillar: PillarItem) => {
    setActivePillar(idx);
    const detailEl = document.getElementById('core-pillar-breakdown');
    if (detailEl && window.innerWidth < 1024) {
      detailEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <section id="services" className="py-24 bg-slate-50 border-b border-slate-200 scroll-mt-12">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-slate-500">
            <span className="text-[#F26522] font-black">THE XWEBA ECOSYSTEM</span>
            <span aria-hidden="true" className="text-slate-300">·</span>
            <span>CORE SERVICES &amp; 20+ YEARS DESIGN HERITAGE</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B2D52] tracking-tight">
            Decades of hands-on brand mastery meets{' '}
            <span className="text-[#F26522]">AI-cited web engineering.</span>
          </h2>

          <p className="text-slate-600 text-base sm:text-lg">
            Led by a <strong>Branding Consultant with over 20 years of hands-on work and design</strong>, our core services unite authoritative brand craft, <strong>Social Media Consulting</strong>, <strong>Digital / Smart Advertisement</strong>, and <strong>Pro WordPress Websites that are cited by AI</strong>.
          </p>
        </div>

        {/* 6 Interactive Core Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            const isSelected = activePillar === idx;
            return (
              <div
                key={pillar.index}
                onClick={() => handleCardClick(idx, pillar)}
                className={`p-6 sm:p-8 rounded-3xl text-left transition-all duration-300 relative border cursor-pointer flex flex-col justify-between group min-w-0 w-full ${
                  isSelected
                    ? 'bg-white border-[#F26522] shadow-xl ring-2 ring-[#F26522]/20'
                    : 'bg-white/90 hover:bg-white border-slate-200 shadow-2xs hover:border-[#F26522]/60 hover:shadow-md'
                }`}
              >
                <div>
                  {/* Clean Editorial Index */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-xs font-black tracking-widest font-mono ${
                      isSelected ? 'text-[#F26522]' : 'text-slate-400 group-hover:text-[#F26522]'
                    }`}>
                      {pillar.index} · {pillar.category}
                    </span>
                    <div className={`p-2.5 rounded-2xl transition-colors ${
                      isSelected ? 'bg-orange-100 text-[#F26522]' : 'bg-slate-100 text-slate-600 group-hover:bg-orange-50 group-hover:text-[#F26522]'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>

                  {/* Site Logo Branded Visual Thumbnail */}
                  <div className="mb-4">
                    <BrandedServiceImage
                      serviceKey={pillar.key}
                      metricBadge={pillar.metric}
                      compact={true}
                    />
                  </div>

                  <h3 className="text-xl sm:text-2xl font-extrabold text-[#0B2D52] mb-1 group-hover:text-[#F26522] transition-colors">
                    {pillar.title}
                  </h3>
                  <p className="text-xs font-bold text-[#F26522] uppercase tracking-wider mb-3">
                    {pillar.subtitle}
                  </p>
                  <p className="text-xs text-slate-600 leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs gap-2">
                  <span className="font-extrabold text-[#0B2D52]">{pillar.metric}</span>
                  {onNavigateToService ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToService(pillar.routeKey);
                      }}
                      className="font-bold text-[#F26522] hover:text-[#0B2D52] flex items-center gap-1 group-hover:translate-x-0.5 transition-all cursor-pointer"
                    >
                      <span>Open Service Page</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <span className="font-bold text-[#F26522] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                      <span>Explore Service</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed Breakdown for the Active Pillar */}
        <div id="core-pillar-breakdown" className="bg-white rounded-3xl border border-slate-200/90 shadow-xl overflow-hidden p-6 sm:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            
            {/* Left Column: Deep Feature List */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 text-xs font-bold text-[#F26522] uppercase tracking-wider">
                  <span>Core Service Deep-Dive</span>
                  <span aria-hidden="true" className="text-slate-300">·</span>
                  <span>{pillars[activePillar].index} / 06</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0B2D52]">
                  {pillars[activePillar].title}: {pillars[activePillar].subtitle}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {pillars[activePillar].description}
                </p>
              </div>

              {/* 5 Distinct Features */}
              <div className="space-y-3 pt-2">
                {pillars[activePillar].features.map((feat, fIdx) => (
                  <div key={fIdx} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                    <CheckCircle2 className="w-4 h-4 text-[#F26522] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold text-[#0B2D52]">{feat.name}: </span>
                      <span className="text-slate-600">{feat.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {onNavigateToService && pillars[activePillar].routeKey && (
                  <button
                    type="button"
                    onClick={() => onNavigateToService(pillars[activePillar].routeKey!)}
                    className="px-6 py-3 bg-[#0B2D52] hover:bg-[#071E36] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                  >
                    <span>Open Dedicated {pillars[activePillar].title} Page</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={onOpenBooking}
                  className="px-6 py-3 bg-[#F26522] hover:bg-[#ea580c] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                >
                  <span>Schedule Consultation</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Right Column: Branded Visual + Key Takeaway */}
            <div className="lg:col-span-5 bg-gradient-to-br from-[#0B2D52] to-[#071E36] text-white rounded-2xl p-6 sm:p-8 space-y-5">
              <BrandedServiceImage
                serviceKey={pillars[activePillar].key}
                metricBadge={pillars[activePillar].metric}
              />

              <div className="space-y-4 text-xs text-slate-200">
                <div className="p-4 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-1.5">
                  <div className="font-semibold text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <span>Business Impact</span>
                  </div>
                  <p className="leading-relaxed">
                    {pillars[activePillar].benefit}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-1.5">
                  <div className="font-semibold text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#F26522]" />
                    <span>2026 Competitive Moat</span>
                  </div>
                  <p className="leading-relaxed">
                    While competitors still rely on passive 5-page brochures and 2015 SEO tactics, your business captures modern conversational searchers and converts them in real time.
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-700/70 text-center">
                <span className="text-[11px] text-slate-400">
                  All 7 core services are available standalone or bundled in WaaS packages.
                </span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};
